import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useLocation } from 'react-router-dom';
import Footer from '../../components/layout/Footer';
import InteractiveDemo from '../../components/landing/InteractiveDemo';
import { getMetaMatchParams } from '../../utils/analytics';
import { useToast } from '../../components/Toast';
import '../features/FeatureLanding.css';
import './PreRegisterLanding.css';
import './TrainingLanding.css';
import './ad-pro/AdProLandingPage.css';
import { TEACHER_COUNT_LABEL } from '../../constants/siteStats';

const SchoolTrialLandingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [form, setForm] = useState({
    schoolName: '',
    schoolType: '',
    schoolRegion: '',
    contactName: '',
    contactRole: '',
    email: '',
    phone: '',
    teacherCount: '',
    reason: '',
    privacyConsent: false,
    marketingConsent: false,
  });

  const [utmParams, setUtmParams] = useState({});
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utm = {};
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'].forEach(key => {
      if (params.get(key)) utm[key] = params.get(key);
    });
    if (!Object.values(utm).some(v => v)) {
      try {
        const stored = localStorage.getItem('whatquiz_utm');
        if (stored) {
          const parsed = JSON.parse(stored);
          if (!parsed.expiresAt || Date.now() <= parsed.expiresAt) {
            ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'].forEach(key => {
              if (parsed[key]) utm[key] = parsed[key];
            });
          }
        }
      } catch (_e) { /* ignore */ }
    }
    setUtmParams(utm);
  }, [location.search]);

  const getApiBaseUrl = () => {
    const host = window.location.hostname;
    if (host.includes('localhost')) return 'http://localhost:8000';
    if (host.includes('whatquiztest')) return 'https://quiz-api-test-397277209875.us-central1.run.app';
    return 'https://quiz-api-397277209875.us-central1.run.app';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const teacherCountNum = parseInt(form.teacherCount, 10);
    const nextErrors = {};
    if (!teacherCountNum || teacherCountNum < 1 || teacherCountNum > 999) {
      nextErrors.teacherCount = '예상 사용 교사 수를 1~999 사이로 입력해주세요';
    }
    if (!form.privacyConsent) {
      nextErrors.privacyConsent = '개인정보 수집·이용에 동의해주세요';
    }
    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setIsSubmitting(true);

    try {
      const response = await fetch(`${getApiBaseUrl()}/api/user/school-trial`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          school_name: form.schoolName,
          school_type: form.schoolType,
          school_region: form.schoolRegion,
          contact_name: form.contactName,
          contact_role: form.contactRole,
          email: form.email,
          phone: form.phone,
          teacher_count: teacherCountNum,
          reason: form.reason || '',
          privacy_consent: form.privacyConsent,
          marketing_consent: form.marketingConsent,
          utm_source: utmParams.utm_source || null,
          utm_medium: utmParams.utm_medium || null,
          utm_campaign: utmParams.utm_campaign || null,
          utm_content: utmParams.utm_content || null,
          ...getMetaMatchParams(),
        })
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || '신청에 실패했습니다.');
      }

      setIsSubmitted(true);

      if (typeof window.gtag !== 'undefined') {
        window.gtag('event', 'school_trial_submit', {
          event_category: 'Conversion',
          event_label: 'school_30day',
          value: teacherCountNum,
          ...utmParams,
        });
      }
      if (typeof window.fbq !== 'undefined') {
        window.fbq('track', 'Lead', {
          content_name: '학교30일무료체험',
          content_category: 'school_trial',
          value: teacherCountNum * 60000,
          currency: 'KRW',
          utm_source: utmParams.utm_source || undefined,
          utm_medium: utmParams.utm_medium || undefined,
          utm_campaign: utmParams.utm_campaign || undefined,
          utm_content: utmParams.utm_content || undefined,
        });
      }
    } catch (error) {
      showToast('신청을 접수하지 못했어요. 잠시 후 다시 시도해주세요', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('school-trial-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  if (isSubmitted) {
    return (
      <div className="feature-landing">
        <Helmet>
          <title>학교 무료체험 신청 완료 | 왓퀴즈</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="preregister-success">
          <div className="preregister-success-inner">
            <div className="preregister-success-icon">
              <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#3182f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
            <h1>신청이 접수됐어요</h1>
            <p>
              영업일 기준 3일 이내 검토 후<br />
              <strong>{form.contactName} 선생님</strong>께 안내드릴게요.<br /><br />
              검토가 완료되면 학교 전체 교사용<br />
              30일 무료체험 공유 쿠폰을 발급해드려요.
            </p>
            <div className="preregister-success-buttons">
              <button className="feature-cta-primary" onClick={() => navigate('/')}>
                왓퀴즈 둘러보기
              </button>
              <button className="feature-cta-secondary" onClick={() => navigate('/library')}>
                자료실 보기
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="feature-landing">
      <Helmet>
        <title>학교 단위 30일 무료체험 신청 | 왓퀴즈AI</title>
        <meta name="description" content="우리 학교 전 교사가 30일 동안 PRO 기능을 무료로. 한 분이 신청하시면 학교 단위 공유 쿠폰 1개를 발급해드려요. KERIS 실증 우수기업, 한국교원대 MOU." />
        <meta name="robots" content="noindex" />
        <link rel="canonical" href="https://whatquiz.co.kr/school" />
      </Helmet>

      {/* 신뢰 바 (소개서 1쪽·26쪽 톤) */}
      <div className="preregister-urgency-bar">
        KERIS 실증 우수기업 · 한국교원대 MOU · S2B/에듀집 등록 · 누적 <strong>{TEACHER_COUNT_LABEL}</strong> 교사
      </div>

      {/* 히어로 */}
      <section className="feature-hero" style={{ background: 'linear-gradient(180deg, #eff6ff 0%, #fff 100%)', paddingTop: '72px' }}>
        <div className="feature-hero-inner">
          <h1>우리 학교 전 교사,<br /><em>30일 동안</em> PRO 무료체험</h1>
          <p className="feature-hero-desc">
            한 분이 신청하시면, 검토 후 학교 단위로<br />
            30일 무료 공유 쿠폰을 발급해드려요.
          </p>
          <div className="training-hero-tags">
            <span className="training-hero-tag">30일 풀 사용</span>
            <span className="training-hero-tag">완전 무료</span>
            <span className="training-hero-tag">학교 단위 공유 쿠폰</span>
          </div>
          <button className="feature-hero-cta" onClick={scrollToForm}>
            30일 무료체험 신청하기
          </button>
          <p className="preregister-hero-sub">자동 결제 없음 · 카드 등록 불필요 · 30일 후 자동 종료</p>
        </div>
      </section>

      {/* 인터랙티브 데모: 5탭 (ad/pro 동일 컴포넌트) */}
      <section className="feature-section" style={{ background: '#fff' }}>
        <div className="feature-section-inner">
          <h2 className="feature-section-title">선생님 업무를 어떻게 줄이는지, 직접 확인해보세요</h2>
          <p className="feature-section-subtitle">탭을 눌러 5가지 핵심 기능 데모를 확인하세요</p>
          <div style={{ marginTop: '32px' }}>
            <InteractiveDemo
              defaultTab={1}
              surface="school"
              ctaLabel="무료로 체험하기"
              onCtaClick={(tab) => {
                if (typeof window.gtag !== 'undefined') {
                  window.gtag('event', 'school_demo_cta_click', {
                    demo_type: tab.label,
                    landing_slug: 'school',
                    ...utmParams,
                  });
                }
                window.open(tab.path, '_blank');
              }}
            />
          </div>
        </div>
      </section>

      {/* 그 외 기능 4종 (소개서 9가지 중 데모에 없는 것: 채팅·공문서·평가계획서·검사기) + 신뢰 라인 */}
      <section className="feature-section" style={{ background: '#f8f9fa' }}>
        <div className="feature-section-inner" style={{ maxWidth: '720px' }}>
          <h2 className="feature-section-title">이 외에도 4가지 기능을 더 제공해요</h2>
          <p className="feature-section-subtitle">소개서에 담긴 9가지 기능 중 핵심 5개는 위에서 데모로 확인하세요</p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '12px',
            marginTop: '32px',
          }}>
            <div style={miniCardStyle}>
              <div style={miniIconStyle}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3182f6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M8 12h8M12 8v8" /></svg>
              </div>
              <div>
                <h3 style={miniTitleStyle}>올인원 AI 채팅</h3>
                <p style={miniDescStyle}>GPT·Claude·Gemini를 한 화면에서 PRO급 무제한</p>
              </div>
            </div>
            <div style={miniCardStyle}>
              <div style={miniIconStyle}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3182f6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
              </div>
              <div>
                <h3 style={miniTitleStyle}>공문서 작성</h3>
                <p style={miniDescStyle}>가정통신문·공문·보고서, HWP 양식 그대로</p>
              </div>
            </div>
            <div style={miniCardStyle}>
              <div style={miniIconStyle}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3182f6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>
              </div>
              <div>
                <h3 style={miniTitleStyle}>AI 평가계획서</h3>
                <p style={miniDescStyle}>성취기준 맞춤 초안, 배점·루브릭까지</p>
              </div>
            </div>
            <div style={miniCardStyle}>
              <div style={miniIconStyle}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3182f6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
              </div>
              <div>
                <h3 style={miniTitleStyle}>AI 생기부 검사기</h3>
                <p style={miniDescStyle}>맞춤법·금지어·바이트 자동 점검</p>
              </div>
            </div>
          </div>
          <div className="training-proof" style={{ marginTop: '32px' }}>
            <span>{TEACHER_COUNT_LABEL} 선생님이 사용 중</span>
            <span className="training-proof-dot"></span>
            <span>KERIS 에듀테크 우수기업</span>
            <span className="training-proof-dot"></span>
            <span>한국교원대 MOU</span>
          </div>
        </div>
      </section>

      {/* 신청 절차 3step */}
      <section className="feature-section" style={{ background: '#f8f9fa' }}>
        <div className="feature-section-inner">
          <h2 className="feature-section-title">신청부터 발급까지 3영업일</h2>
          <p className="feature-section-subtitle">학교 사칭/허위 신청 방지를 위해 영업팀이 직접 검토해요</p>
          <div className="preregister-benefits">
            <div className="preregister-benefit">
              <div className="preregister-benefit-number">
                <span style={{ fontSize: '18px', fontWeight: 700, color: '#3182f6' }}>1</span>
              </div>
              <div className="preregister-benefit-text">
                <h3>학교 대표 한 분이 신청</h3>
                <p>학교명, 담당자, 예상 인원, 학교 이메일을 입력해주세요. 1분이면 끝나요.</p>
              </div>
            </div>
            <div className="preregister-benefit">
              <div className="preregister-benefit-number">
                <span style={{ fontSize: '18px', fontWeight: 700, color: '#3182f6' }}>2</span>
              </div>
              <div className="preregister-benefit-text">
                <h3>왓퀴즈 영업팀 검토 (영업일 기준 3일 이내)</h3>
                <p>학교 정보를 확인하고 담당자께 메일·전화로 안내드려요. 견적서·서류는 신청 즉시 메일로 발송돼요.</p>
              </div>
            </div>
            <div className="preregister-benefit">
              <div className="preregister-benefit-number">
                <span style={{ fontSize: '18px', fontWeight: 700, color: '#3182f6' }}>3</span>
              </div>
              <div className="preregister-benefit-text">
                <h3>학교 단위 공유 쿠폰 발급</h3>
                <p>30일 PRO 공유 쿠폰 1개를 발급해드려요. 신청 인원 수만큼 등록할 수 있고, 교사들에게 공유할 안내문도 함께 보내드려요.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="feature-section">
        <div className="feature-section-inner" style={{ maxWidth: '720px' }}>
          <h2 className="feature-section-title">자주 묻는 질문</h2>
          <div className="preregister-info-block" style={{ marginTop: '20px' }}>
            <div style={{ paddingBottom: '16px', marginBottom: '16px', borderBottom: '1px solid #f2f4f6' }}>
              <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#191f28', margin: '0 0 6px' }}>학교 도메인 이메일이 아니어도 되나요?</h3>
              <p style={{ fontSize: '14px', color: '#6b7684', margin: 0, lineHeight: 1.6 }}>네, 일반 이메일도 가능합니다. 다만 검토에 시간이 더 걸릴 수 있어요. 학교 이메일(@*.sch.kr / @*.go.kr)이면 더 빠르게 발급해드려요.</p>
            </div>
            <div style={{ paddingBottom: '16px', marginBottom: '16px', borderBottom: '1px solid #f2f4f6' }}>
              <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#191f28', margin: '0 0 6px' }}>30일 후에 자동 결제되나요?</h3>
              <p style={{ fontSize: '14px', color: '#6b7684', margin: 0, lineHeight: 1.6 }}>아니요, 카드 등록이 없으니 자동 결제가 절대 발생하지 않아요. 30일이 지나면 자연스럽게 무료 플랜으로 돌아갑니다.</p>
            </div>
            <div style={{ paddingBottom: '16px', marginBottom: '16px', borderBottom: '1px solid #f2f4f6' }}>
              <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#191f28', margin: '0 0 6px' }}>교사 명단을 따로 보내야 하나요?</h3>
              <p style={{ fontSize: '14px', color: '#6b7684', margin: 0, lineHeight: 1.6 }}>아니요. 승인 후 보내드리는 학교 단위 공유 쿠폰 1개를 대표 선생님께서 교내 메신저로 공유해주시면 돼요. 신청 인원 수만큼 등록할 수 있고, 선생님 한 분당 1회 사용 가능합니다.</p>
            </div>
            <div>
              <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#191f28', margin: '0 0 6px' }}>견적서를 받을 수 있나요? 학운위 심의도 필요한가요?</h3>
              <p style={{ fontSize: '14px', color: '#6b7684', margin: 0, lineHeight: 1.6 }}>신청 즉시 견적서·품의서·사업자등록증·통장사본을 메일로 보내드려요. 왓퀴즈는 에듀집(KERIS) 검토 결과 학운위 심의 대상이 아니라서, 30일 사용 후 품의만 올리시면 학교 단위로 바로 도입할 수 있어요.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 신청 폼 */}
      <section className="feature-section" style={{ background: '#f8f9fa' }} id="school-trial-form">
        <div className="feature-section-inner">
          <h2 className="feature-section-title">30일 무료체험 신청</h2>
          <p className="feature-section-subtitle">학교 대표 선생님 한 분이 신청해주세요</p>

          <form className="preregister-form" onSubmit={handleSubmit}>
            <div className="preregister-form-row">
              <div className="preregister-form-field">
                <label>학교명 *</label>
                <input
                  type="text" value={form.schoolName}
                  onChange={(e) => setForm({ ...form, schoolName: e.target.value })}
                  required placeholder="왓퀴즈초등학교"
                />
              </div>
              <div className="preregister-form-field">
                <label>학교 종류 *</label>
                <select
                  value={form.schoolType}
                  onChange={(e) => setForm({ ...form, schoolType: e.target.value })}
                  required
                  style={{ width: '100%', padding: '12px 14px', border: '1px solid #e5e7eb', borderRadius: '10px', fontSize: '15px', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', background: '#fff' }}
                >
                  <option value="">선택해주세요</option>
                  <option value="초등학교">초등학교</option>
                  <option value="중학교">중학교</option>
                  <option value="고등학교">고등학교</option>
                  <option value="특수학교">특수학교</option>
                  <option value="기타">기타</option>
                </select>
              </div>
            </div>
            <div className="preregister-form-row">
              <div className="preregister-form-field">
                <label>학교 소재지 *</label>
                <select
                  value={form.schoolRegion}
                  onChange={(e) => setForm({ ...form, schoolRegion: e.target.value })}
                  required
                  style={{ width: '100%', padding: '12px 14px', border: '1px solid #e5e7eb', borderRadius: '10px', fontSize: '15px', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', background: '#fff' }}
                >
                  <option value="">선택해주세요</option>
                  <option value="서울">서울</option>
                  <option value="경기">경기</option>
                  <option value="인천">인천</option>
                  <option value="부산">부산</option>
                  <option value="대구">대구</option>
                  <option value="대전">대전</option>
                  <option value="광주">광주</option>
                  <option value="울산">울산</option>
                  <option value="세종">세종</option>
                  <option value="강원">강원</option>
                  <option value="충북">충북</option>
                  <option value="충남">충남</option>
                  <option value="전북">전북</option>
                  <option value="전남">전남</option>
                  <option value="경북">경북</option>
                  <option value="경남">경남</option>
                  <option value="제주">제주</option>
                </select>
              </div>
              <div className="preregister-form-field">
                <label>예상 사용 교사 수 *</label>
                <input
                  type="number" value={form.teacherCount}
                  onChange={(e) => {
                    setForm({ ...form, teacherCount: e.target.value });
                    if (fieldErrors.teacherCount) setFieldErrors((prev) => ({ ...prev, teacherCount: undefined }));
                  }}
                  required min="1" max="999" placeholder="30"
                  aria-invalid={fieldErrors.teacherCount ? 'true' : undefined}
                />
                {fieldErrors.teacherCount && (
                  <p className="preregister-field-error" role="alert">{fieldErrors.teacherCount}</p>
                )}
              </div>
            </div>
            <div className="preregister-form-row">
              <div className="preregister-form-field">
                <label>담당자 성함 *</label>
                <input
                  type="text" value={form.contactName}
                  onChange={(e) => setForm({ ...form, contactName: e.target.value })}
                  required placeholder="김왓퀴"
                />
              </div>
              <div className="preregister-form-field">
                <label>직책 *</label>
                <select
                  value={form.contactRole}
                  onChange={(e) => setForm({ ...form, contactRole: e.target.value })}
                  required
                  style={{ width: '100%', padding: '12px 14px', border: '1px solid #e5e7eb', borderRadius: '10px', fontSize: '15px', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', background: '#fff' }}
                >
                  <option value="">선택해주세요</option>
                  <option value="교사">교사</option>
                  <option value="부장교사">부장교사</option>
                  <option value="교감">교감</option>
                  <option value="교장">교장</option>
                  <option value="행정실장">행정실장</option>
                  <option value="기타">기타</option>
                </select>
              </div>
            </div>
            <div className="preregister-form-row">
              <div className="preregister-form-field">
                <label>학교 이메일 *</label>
                <input
                  type="email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required placeholder="@sch.kr / @go.kr 권장"
                />
              </div>
              <div className="preregister-form-field">
                <label>휴대폰 *</label>
                <input
                  type="tel" value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  required placeholder="010-0000-0000"
                />
              </div>
            </div>
            <div className="preregister-form-row" style={{ gridTemplateColumns: '1fr' }}>
              <div className="preregister-form-field">
                <label>도입 검토 사유 (선택)</label>
                <input
                  type="text" value={form.reason}
                  onChange={(e) => setForm({ ...form, reason: e.target.value })}
                  placeholder="예: 전 교사 대상 디지털 역량 강화, 생기부 시즌 대비 등"
                />
              </div>
            </div>

            <div style={{ background: '#f8f9fa', borderRadius: '10px', padding: '14px 16px', marginTop: '8px', marginBottom: '4px' }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', fontSize: '13px', color: '#4e5968', lineHeight: 1.5 }}>
                <input
                  type="checkbox" checked={form.privacyConsent}
                  onChange={(e) => {
                    setForm({ ...form, privacyConsent: e.target.checked });
                    if (e.target.checked && fieldErrors.privacyConsent) setFieldErrors((prev) => ({ ...prev, privacyConsent: undefined }));
                  }}
                  style={{ marginTop: '2px', width: '16px', height: '16px', accentColor: '#3182f6', flexShrink: 0 }}
                  aria-invalid={fieldErrors.privacyConsent ? 'true' : undefined}
                />
                <span>
                  <strong style={{ color: '#191f28' }}>[필수]</strong> 개인정보 수집·이용 동의 (학교명, 담당자명, 이메일, 휴대폰을 무료체험 신청 검토·안내 목적으로 수집하며, 발급일로부터 1년간 보관 후 파기합니다.)
                </span>
              </label>
              {fieldErrors.privacyConsent && (
                <p className="preregister-field-error" role="alert" style={{ marginTop: '8px' }}>{fieldErrors.privacyConsent}</p>
              )}
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', fontSize: '13px', color: '#4e5968', lineHeight: 1.5, marginTop: '10px' }}>
                <input
                  type="checkbox" checked={form.marketingConsent}
                  onChange={(e) => setForm({ ...form, marketingConsent: e.target.checked })}
                  style={{ marginTop: '2px', width: '16px', height: '16px', accentColor: '#3182f6', flexShrink: 0 }}
                />
                <span>
                  <strong style={{ color: '#6b7684' }}>[선택]</strong> 마케팅 정보 수신 동의 (왓퀴즈 신기능, 교사 대상 이벤트 안내)
                </span>
              </label>
            </div>

            <button type="submit" className="preregister-submit" disabled={isSubmitting}>
              {isSubmitting ? '신청 중...' : '30일 무료체험 신청하기'}
            </button>
            <p className="preregister-form-note">
              신청 후 영업일 기준 3일 이내에 검토 결과를 안내드려요.<br />
              견적서·서류는 신청 즉시 이메일로 발송됩니다.
            </p>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const miniCardStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '12px',
  padding: '16px',
  background: '#fff',
  borderRadius: '12px',
  border: '1px solid #e5e7eb',
};

const miniIconStyle = {
  width: '36px',
  height: '36px',
  background: '#e8f3ff',
  borderRadius: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const miniTitleStyle = {
  fontSize: '14px',
  fontWeight: 700,
  color: '#191f28',
  margin: '0 0 4px',
};

const miniDescStyle = {
  fontSize: '12px',
  color: '#6b7684',
  margin: 0,
  lineHeight: 1.5,
};

export default SchoolTrialLandingPage;
